import { buildSchedulePickupRequest } from '../../../../../core/api/pickup/mappers/build-schedule-pickup-request';
import type {
  PickupTransportationMode,
  SchedulePickupRequest,
} from '../../../../../core/api/pickup/models/pickup.model';
import type { AddressFormValue } from '../../../../ship-from-ship-to/models/address-form.model';
import {
  isPickupDropOffValid,
  mapShipFromToPickupAddress,
  type PickupDropOffState,
} from '../pickup-drop-off.state';
import { isSelectablePickupLocationCode } from './build-pickup-location-options';

/** Inputs for preparing the downstream `POST /pickup/schedule` body from Service-step state. */
export interface PrepareSchedulePickupRequestInput {
  state: PickupDropOffState;
  shipFrom: AddressFormValue;
  currentLocalTime: string;
  transportationMode: PickupTransportationMode;
  destinationCode: string;
}

/**
 * Builds the Salesforce Schedule Pickup request DTO from the active pickup selection.
 * Returns null when the method is not pickup, the selection is incomplete, or the location
 * code is missing/unmapped — so Drop-off never prepares a schedule body.
 */
export function prepareSchedulePickupRequest(
  input: PrepareSchedulePickupRequestInput,
): SchedulePickupRequest | null {
  const { state, shipFrom, currentLocalTime, transportationMode, destinationCode } = input;
  if (state.method !== 'pickup' || !isPickupDropOffValid(state)) {
    return null;
  }
  if (
    !state.selectedDate ||
    !state.windowFrom ||
    !state.windowTo ||
    !state.locationCode ||
    !isSelectablePickupLocationCode(state.locationCode, state.pickupFromMap)
  ) {
    return null;
  }

  return buildSchedulePickupRequest({
    pickupDate: state.selectedDate,
    currentLocalTime,
    pickupAddress: mapShipFromToPickupAddress(shipFrom),
    transportationMode,
    destinationCode,
    pickUpAnyTimeAfter: state.windowFrom,
    pickUpUntilTime: state.windowTo,
    pickUpLocationCode: state.locationCode,
    additionalInstructions: state.courierInstructions || undefined,
  });
}
